import { ColumnOptions, Item } from '@/types/ColumnOptions'
import {
  ColumnFilterConfiguration,
  PaginatedItem,
  RuleBuilderConfiguration
} from '@/types/CustomDataTable'
import { ConditionField, ParameterField } from '@/types/ProductInsights'
import { ProductInsightFields } from './productInsights'
import { ProductStatus } from './ProductStatus'
import { DrawerTab } from '@/types/NavigationDrawer'
import { ProductFieldSection, ProductVariant } from '@/types/productVariants'
import { FeedMappingEntry } from '@/types/roimaticConfigurations'

export const ProductVariantFields = {
  ...ProductInsightFields,
  ProductVariantPrice: 162,
  ProductVariantSalePrice: 163,
  ProductVariantDiscount: 164,
  ProductVariantRating: 165,
  SalePriceDrop: 166,
  ItemType: 183,
  ProductActions: 10002
}
export const defaultHeadersVariantIds = [
  ProductVariantFields.ProductVariantStatus,
  ProductVariantFields.ItemType,
  ProductVariantFields.ProductVariantImageLink,
  ProductVariantFields.ProductVariantTitle,
  ProductVariantFields.ProductVariantDescription,
  ProductVariantFields.ProductVariantSize,
  ProductVariantFields.ProductVariantAvailability,
  ProductVariantFields.ProductVariantPrice
]

export const REQUIRED_PRODUCT_PARAMETER_IDS = [
  ProductInsightFields.ItemGroupId,
  ProductInsightFields.ItemId,
  ProductVariantFields.ProductBrand,
  ProductVariantFields.ProductVariantSalePrice,
  ProductVariantFields.ProductType,
  ProductInsightFields.ProductVariantDescription,
  ProductInsightFields.ProductVariantTitle
]
export const ATTRIBUTES: Item[] = [
  {
    title: 'Status',
    key: 'ProductVariantStatus',
    sortable: false,
    id: ProductVariantFields.ProductVariantStatus,
    minWidth: '120px',
    align: 'center',
    fixed: true,
    isSelectionLocked: true
  },
  {
    title: 'Item Type',
    key: 'itemType',
    sortable: false,
    id: ProductVariantFields.ItemType,
    minWidth: '120px',
    align: 'center',
    fixed: true,
    isSelectionLocked: true,
    nowrap: true
  },
  {
    title: 'Image',
    key: 'ProductVariantImageLink',
    sortable: false,
    id: ProductVariantFields.ProductVariantImageLink,
    minWidth: '100px',
    align: 'center',
    fixed: true,
    isSelectionLocked: true
  },
  {
    title: 'Product Title',
    key: 'ProductVariantTitle',
    sortable: true,
    id: ProductVariantFields.ProductVariantTitle,
    minWidth: '250px',
    maxWidth: '250px',
    width: '250px',
    align: 'left',
    fixed: true,
    isSelectionLocked: true
  },
  {
    title: 'Product Description',
    key: 'ProductVariantDescription',
    sortable: true,
    id: ProductVariantFields.ProductVariantDescription,
    minWidth: '250px',
    maxWidth: '250px',
    width: '250px',
    align: 'left'
  },
  {
    title: 'Size',
    key: 'ProductVariantSize',
    sortable: false,
    id: ProductVariantFields.ProductVariantSize,
    minWidth: '100px',
    align: 'center'
  },
  {
    title: 'Color',
    key: 'ProductVariantColor',
    sortable: false,
    id: ProductVariantFields.ProductVariantColor,
    minWidth: '100px',
    align: 'center'
  },
  {
    title: 'Availability',
    key: 'ProductVariantAvailability',
    sortable: false,
    id: ProductVariantFields.ProductVariantAvailability,
    minWidth: '100px',
    align: 'center'
  },
  {
    title: 'Price (₹)',
    key: 'ProductVariantPrice',
    sortable: true,
    id: ProductVariantFields.ProductVariantPrice,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Category',
    key: 'ProductCategory',
    sortable: true,
    id: ProductVariantFields.ProductCategory,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Product Type',
    key: 'productType',
    sortable: true,
    id: ProductVariantFields.ProductType,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Brand',
    key: 'ProductBrand',
    sortable: true,
    id: ProductInsightFields.ProductBrand,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Material',
    key: 'ProductVariantMaterial',
    sortable: true,
    id: ProductVariantFields.ProductVariantMaterial,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Gender',
    key: 'ProductVariantGender',
    sortable: true,
    id: ProductVariantFields.ProductVariantGender,
    minWidth: '120px',
    align: 'center'
  },
  {
    title: 'Inventory Count',
    key: 'inventoryCount',
    sortable: true,
    id: ProductVariantFields.ProductInventoryCount,
    minWidth: '120px',
    align: 'center'
  }
]

export const SELECTION_OPTIONS: string[] = ['Attributes']

export const ATTRIBUTE_OPTIONS: ColumnOptions[] = [
  {
    id: 1,
    title: 'Attributes',
    items: ATTRIBUTES
  }
]

export const COLUMN_FILTER_CONFIGURATION: ColumnFilterConfiguration = {
  selectionOptions: SELECTION_OPTIONS,
  attributeOptions: ATTRIBUTE_OPTIONS,
  metricOptions: []
}

export const ALLOWED_ATTRIBUTES: ConditionField[] = [
  {
    name: 'Product Variant Title',
    value: ProductVariantFields.ProductVariantTitle
  },
  {
    name: 'Product Variant Description',
    value: ProductVariantFields.ProductVariantDescription
  },
  {
    name: 'Product Variant Price',
    value: ProductVariantFields.ProductVariantPrice
  },
  { name: 'Item Type', value: ProductVariantFields.ItemType },
  { name: 'Product Size', value: ProductVariantFields.ProductVariantSize },
  { name: 'Category', value: ProductVariantFields.ProductCategory },
  { name: 'Product Type', value: ProductVariantFields.ProductType },
  {
    name: 'Product Variant Image',
    value: ProductVariantFields.ProductVariantImageLink
  },
  {
    name: 'Availability',
    value: ProductVariantFields.ProductVariantAvailability
  },
  { name: 'Color', value: ProductVariantFields.ProductVariantColor },
  { name: 'Brand', value: ProductVariantFields.ProductBrand },
  { name: 'Material', value: ProductVariantFields.ProductVariantMaterial },
  { name: 'Gender', value: ProductVariantFields.ProductVariantGender },
  {
    name: 'Inventory Count',
    value: ProductVariantFields.ProductInventoryCount
  },
  { name: 'Item Id', value: ProductVariantFields.ItemId }
]

export const RULE_BUILDER_CONFIGURATION: RuleBuilderConfiguration = {
  allowedParameterIds: [...ALLOWED_ATTRIBUTES].map(parameter => parameter.value)
}

export enum ItemType {
  Product = 1,
  Variant = 2
}
export const ItemTypeConfig = {
  [ItemType.Product]: {
    color: 'accent',
    label: 'Product'
  },
  [ItemType.Variant]: {
    color: 'info',
    label: 'Variant'
  }
}

// Product Status Filters
export const PRODUCT_STATUS_DROPDOWN_FILTER: ProductStatus[] = [
  ProductStatus.Active,
  ProductStatus.Restricted,
  ProductStatus.Excluded
]

export const PRODUCT_STATUS_FILTER_LIST = [
  { title: 'Active', value: ProductStatus.Active },
  { title: 'Excluded', value: ProductStatus.Excluded },
  { title: 'Restricted', value: ProductStatus.Restricted }
]

// Item Type Filters
export const ITEM_TYPE_DROPDOWN_FILTER: ItemType[] = [
  ItemType.Product,
  ItemType.Variant
]

export const ITEM_TYPE_FILTERS = [
  { title: 'Product', value: ItemType.Product },
  { title: 'Variant', value: ItemType.Variant }
]

export enum VariantListingDrawerTabs {
  Details = 1,
  Creatives = 2,
  Content = 3,
  Issues = 4
}

export const variantListingDrawer: DrawerTab[] = [
  {
    value: VariantListingDrawerTabs.Details,
    label: 'labels.productVariantDrawerDetails',
    slot: 'details'
  },
  {
    value: VariantListingDrawerTabs.Creatives,
    label: 'labels.productVariantDrawerCreatives',
    slot: 'creatives'
  },
  {
    value: VariantListingDrawerTabs.Content,
    label: 'labels.productVariantDrawerContent',
    slot: 'content'
  },
  {
    value: VariantListingDrawerTabs.Issues,
    label: 'labels.productVariantDrawerIssues',
    slot: 'issues',
    color: 'error'
  }
]

export const VariantListingContentSection: ProductFieldSection[] = [
  {
    label: 'labels.feedTitle',
    enrichedLabel: 'labels.aiEnhancedTitle',
    noEnrichTitle: 'labels.aiTitleNotGenerated',
    noEnrichDescription: 'messages.aiTitleNotGeneratedDescription',
    feedKey: 'enrichedTitle',
    enrichedKey: 'ProductVariantTitle',
    icon: 'mdi-format-title'
  },
  {
    label: 'labels.feedDescription',
    enrichedLabel: 'labels.aiEnhancedDescription',
    noEnrichTitle: 'labels.aiDescriptionNotGenerated',
    noEnrichDescription: 'messages.aiDescriptionNotGeneratedDescription',
    feedKey: 'enrichedDescription',
    enrichedKey: 'ProductVariantDescription',
    icon: 'mdi-text'
  }
]

export const ExcludedProductVariantFieldsInRuleBuilder = [
  ProductVariantFields.ProductPrice,
  ProductVariantFields.SalePriceDrop,
  ProductVariantFields.ProductSalePrice,
  ProductVariantFields.ProductStatus
]

export const getAllowedParameterIdsForVariants = (
  parameterFields: ParameterField[],
  excludedFields: number[] = []
): number[] => {
  return parameterFields
    .filter(
      p =>
        (p.tableName === 'Products' ||
          p.tableName === 'ProductGroups' ||
          p.tableName === 'ProductTags') &&
        !ExcludedProductVariantFieldsInRuleBuilder.includes(p.id) &&
        !excludedFields.includes(p.id)
    )
    .map(p => p.id)
}

export const defaultProductVariantResponse: PaginatedItem<ProductVariant> = {
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  data: []
}

/**
 * Maps a FeedMapping DestinationColumn name to the corresponding
 * ProductVariant field key in the ATTRIBUTES list.
 */
export const DESTINATION_COLUMN_TO_ATTRIBUTE: Record<string, string> = {
  Id: 'searchEngineProductId',
  Title: 'title',
  Description: 'description',
  Price: 'price',
  SalePrice: 'salePrice',
  Brand: 'brand',
  Color: 'color',
  Size: 'size',
  Gender: 'gender',
  Material: 'material',
  Availability: 'availability',
  GoogleProductCategory: 'category',
  ProductType: 'productType',
  ImageLink: 'productImageLink',
  ItemGroupId: 'productGroupId',
  Pattern: 'pattern',
  CustomLabel0: 'customLabel0',
  CustomLabel1: 'customLabel1',
  CustomLabel2: 'customLabel2',
  CustomLabel3: 'customLabel3',
  CustomLabel4: 'customLabel4'
}

/** Always-locked columns that appear regardless of FeedMapping */
const LOCKED_COLUMNS: Item[] = [
  {
    title: 'Status',
    key: 'ProductVariantStatus',
    sortable: false,
    id: ProductVariantFields.ProductVariantStatus,
    minWidth: '120px',
    align: 'center',
    isSelectionLocked: true,
    fixed: true
  },
  {
    title: 'Item Type',
    key: 'itemType',
    sortable: false,
    id: ProductVariantFields.ItemType,
    minWidth: '120px',
    align: 'center',
    isSelectionLocked: true,
    nowrap: true,
    fixed: true
  },
  {
    title: 'Image',
    key: 'ProductImageLink',
    sortable: false,
    id: ProductVariantFields.ProductVariantImageLink,
    minWidth: '100px',
    align: 'center',
    isSelectionLocked: true,
    fixed: true
  }
]

/**
 * Builds the ATTRIBUTES Item[] dynamically from the channel's FeedMapping.
 * Locked columns (Status, ItemType, Image) are always included.
 * Only DestinationColumn entries with a known mapping are turned into columns.
 * Falls back to the static ATTRIBUTES list when feedMapping is null/empty.
 */
export const buildDynamicAttributes = (
  mappings: ParameterField[],
  defaultIds: number[] = []
): Item[] => {
  const usedIds = new Set<number>()
  const attributes: Item[] = []

  // Always include default columns first.
  defaultIds.forEach(id => {
    const attribute = ATTRIBUTES.find(item => Number(item.id) === Number(id))

    if (attribute) {
      attributes.push(attribute)
      usedIds.add(Number(id))
    }
  })

  // Then add destination-channel mapped columns.
  mappings
    .filter(mapping => mapping.id != null)
    .forEach(mapping => {
      const parameterId = Number(mapping.id)

      if (Number.isNaN(parameterId) || usedIds.has(parameterId)) {
        return
      }

      usedIds.add(parameterId)

      const fixedAttribute = ATTRIBUTES.find(
        attribute => attribute.id === parameterId
      )

      if (fixedAttribute) {
        attributes.push({
          ...fixedAttribute
        })
        return
      }

      attributes.push({
        id: parameterId,
        key: mapping.alias,
        title: mapping.name
          .replace(/^Product Variant\s*/i, '')
          .replace(/^Product\s*/i, '')
          .trim(),
        sortable: false,
        minWidth: '150px',
        align: 'center'
      })
    })

  return attributes
}

export const FIXED_PARAMETER_IDS = new Set([
  ProductVariantFields.ProductVariantStatus,
  ProductVariantFields.ItemType,
  ProductVariantFields.ProductVariantImageLink,
  ProductVariantFields.ProductVariantTitle,
  ProductVariantFields.ProductVariantDescription
])

export const FIXED_RULE_BUILDER_IDS = [
  ProductVariantFields.StopLossExclusionByRule
]

export const IMAGE_OPTIMIZATION_STATE = {
  noOverlayRemovalApplied: {
    icon: '',
    color: 'grey',
    title: 'labels.catalogManager.noOverlayRemovalApplied',
    description: 'messages.catalogManager.noOverlayRemovalAppliedDescription',
    showButton: true,
    class: 'not-enriched-details border-warning'
  },

  detectedNotApplied: {
    icon: 'mdi-alert-outline',
    color: 'warning',
    title: 'labels.catalogManager.overlayDetected',
    description:
      'messages.catalogManager.overlayDetectedButNotAppliedDescription',
    showButton: false,
    class: 'border-warning'
  },

  noOverlaydetected: {
    icon: 'mdi-check-circle-outline',
    color: 'success',
    title: 'labels.catalogManager.noOverlayDetected',
    description: 'messages.catalogManager.noOverlayDetectedDescription',
    showButton: false,
    class: ''
  }
} as const
